import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, TrendingUp, AlertTriangle, ShieldAlert, Zap,
  Percent, Coins, Package, Users, Check, X, Pencil,
} from "lucide-react";
import { adminAxios } from "./adminApi";

/* ─────────────────── Small building blocks ─────────────────── */

function StatCard({ label, value, sub, tone = "default", testId }) {
  const toneClass = {
    default: "border-lm-border bg-lm-surface",
    good: "border-emerald-200 bg-emerald-50/50",
    warn: "border-amber-200 bg-amber-50/50",
    bad: "border-red-200 bg-red-50/50",
  }[tone] || "border-lm-border bg-lm-surface";
  return (
    <div data-testid={testId} className={`rounded-xl border ${toneClass} p-4`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">{label}</div>
      <div className="mt-1.5 font-display font-semibold text-2xl tracking-tight text-lm-text">{value}</div>
      {sub && <div className="mt-1 text-xs text-lm-text2">{sub}</div>}
    </div>
  );
}

function AlarmChip({ triggered, label, icon: Icon, threshold, current, testId }) {
  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        triggered
          ? "bg-red-50 border-red-200 text-red-800"
          : "bg-lm-tint border-lm-border text-lm-text3"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className="font-mono text-[10px] opacity-80">{current} / {threshold}</span>
      {triggered && <span className="font-semibold uppercase tracking-widest">⚠</span>}
    </div>
  );
}

function fmtINR(n, decimals = 2) {
  if (n === null || n === undefined) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/* ─────────────────── Vendor price editor row ─────────────────── */

function VendorRow({ v, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    unit_price_usd: v.unit_price_usd,
    unit_type: v.unit_type,
    use_case: v.use_case || "",
    notes: v.notes || "",
  });
  const cancel = () => {
    setForm({
      unit_price_usd: v.unit_price_usd,
      unit_type: v.unit_type,
      use_case: v.use_case || "",
      notes: v.notes || "",
    });
    setEditing(false);
  };
  const save = async () => {
    setBusy(true);
    try {
      await adminAxios().patch(`/economics/vendor-prices/${v.vendor_id}`, {
        unit_price_usd: parseFloat(form.unit_price_usd),
        unit_type: form.unit_type,
        use_case: form.use_case,
        notes: form.notes,
      });
      toast.success(`Updated ${v.vendor_name}`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <tr
      data-testid={`vendor-row-${v.vendor_id}`}
      className="border-t border-lm-border/60 hover:bg-lm-tint/20"
    >
      <td className="px-3 py-2.5 align-top">
        <div className="font-medium text-lm-text">{v.vendor_name}</div>
        <div className="text-[10px] text-lm-text3 font-mono">{v.vendor_id}</div>
        {v.affects_scan_cogs && (
          <span className="mt-1 inline-block text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-lm-tint2 text-lm-primary">
            in scan cogs
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 align-top">
        {editing ? (
          <input
            data-testid={`vendor-price-input-${v.vendor_id}`}
            type="number"
            step="0.00001"
            min="0"
            value={form.unit_price_usd}
            onChange={(e) => setForm({ ...form, unit_price_usd: e.target.value })}
            className="w-28 px-2 py-1 text-sm border border-lm-border rounded font-mono"
          />
        ) : (
          <span className="font-mono text-sm">${v.unit_price_usd}</span>
        )}
      </td>
      <td className="px-3 py-2.5 align-top text-xs text-lm-text2 font-mono">
        {editing ? (
          <input
            value={form.unit_type}
            onChange={(e) => setForm({ ...form, unit_type: e.target.value })}
            className="w-32 px-2 py-1 border border-lm-border rounded font-mono text-xs"
          />
        ) : v.unit_type}
      </td>
      <td className="px-3 py-2.5 align-top text-xs text-lm-text">
        {editing ? (
          <input
            value={form.use_case}
            onChange={(e) => setForm({ ...form, use_case: e.target.value })}
            className="w-full min-w-[220px] px-2 py-1 border border-lm-border rounded text-xs"
          />
        ) : v.use_case || "—"}
      </td>
      <td className="px-3 py-2.5 align-top text-[10px] text-lm-text3 font-mono">
        {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : "—"}
        {v.updated_by && <div>by {v.updated_by}</div>}
      </td>
      <td className="px-3 py-2.5 align-top text-right">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <button
              data-testid={`vendor-save-${v.vendor_id}`}
              onClick={save}
              disabled={busy}
              className="p-1.5 rounded bg-lm-primary text-white hover:bg-lm-primaryHover disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={cancel}
              disabled={busy}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-60"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            data-testid={`vendor-edit-${v.vendor_id}`}
            onClick={() => setEditing(true)}
            className="p-1.5 rounded text-lm-text2 hover:text-lm-text hover:bg-lm-tint"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

/* ─────────────────── Main page ─────────────────── */

export default function AdminEconomics() {
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: v }] = await Promise.all([
        adminAxios().get("/economics/summary", { params: { days } }),
        adminAxios().get("/economics/vendor-prices"),
      ]);
      setSummary(s);
      setVendors(v.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load economics");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-economics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-lm-primary" />
      </div>
    );
  }

  const cogsTone =
    summary.cogs.effective_inr_per_scan > summary.thresholds.cost_alarm_inr ? "bad" :
    summary.cogs.effective_inr_per_scan > summary.cogs.target_inr_per_scan ? "warn" : "good";
  const rescueTone =
    summary.rescue.rate_pct > summary.thresholds.rescue_rate_pct ? "bad" :
    summary.rescue.rate_pct > 15 ? "warn" : "good";
  const ltdMedian = summary.cohort_utilization_month_to_date?.ltd?.median_pct || 0;
  const ltdTone =
    ltdMedian > summary.thresholds.ltd_risk_pct ? "bad" :
    ltdMedian > 40 ? "warn" : "good";

  return (
    <div data-testid="admin-economics">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Cost / Margin / Vendor prices</div>
          <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">Economics</h1>
          <p className="text-sm text-lm-text2 mt-1">
            Live COGS from `vendor_prices`. Update a vendor price and everything below recalculates on refresh.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="economics-window-select"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-xs border border-lm-border rounded-md px-3 py-2 bg-lm-surface"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={load}
            data-testid="economics-refresh"
            className="inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text2 border border-lm-border rounded-md px-3 py-2 hover:bg-lm-tint2"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Leak banner */}
      {summary.leaks.orphan_entries > 0 && (
        <div
          data-testid="leak-banner"
          className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 flex items-start gap-3"
        >
          <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-900">
              Billing leak: {summary.leaks.orphan_entries} UsageLog {summary.leaks.orphan_entries === 1 ? "entry" : "entries"} with no scan_id
            </div>
            <div className="text-xs text-red-800 mt-1">
              External API cost recorded but not attributable to a scan. Fix the caller so every usage_logs row carries `scan_id`.
            </div>
          </div>
        </div>
      )}

      {/* Alarms row */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="economics-alarms">
        <AlarmChip
          testId="alarm-cost"
          triggered={summary.alarms.cost_alarm}
          label="Cost alarm"
          icon={Coins}
          threshold={`>${fmtINR(summary.thresholds.cost_alarm_inr, 0)}`}
          current={fmtINR(summary.cogs.effective_inr_per_scan, 2)}
        />
        <AlarmChip
          testId="alarm-ltd"
          triggered={summary.alarms.ltd_risk}
          label="LTD risk"
          icon={Users}
          threshold={`>${summary.thresholds.ltd_risk_pct}%`}
          current={`${ltdMedian}%`}
        />
        <AlarmChip
          testId="alarm-pipeline"
          triggered={summary.alarms.pipeline_quality}
          label="Pipeline quality"
          icon={AlertTriangle}
          threshold={`>${summary.thresholds.rescue_rate_pct}%`}
          current={`${summary.rescue.rate_pct}%`}
        />
        {summary.burst_pressure?.plans?.map((p) => p.burst_pressure_alarm ? (
          <AlarmChip
            key={p.plan_key}
            testId={`alarm-burst-${p.plan_key}`}
            triggered={true}
            label={`${p.plan_key} burst pressure — consider raising cap`}
            icon={Zap}
            threshold=">30%"
            current={`${p.pct_hitting_burst}%`}
          />
        ) : null)}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard
          testId="kpi-cogs"
          label="Actual ₹/scan"
          value={fmtINR(summary.cogs.effective_inr_per_scan, 2)}
          sub={summary.cogs.is_estimated
            ? `Estimated · target ${fmtINR(summary.cogs.target_inr_per_scan, 0)}`
            : `Real usage · target ${fmtINR(summary.cogs.target_inr_per_scan, 0)}`}
          tone={cogsTone}
        />
        <StatCard
          testId="kpi-rescue"
          label="% Scans rescued"
          value={`${summary.rescue.rate_pct}%`}
          sub={`${summary.rescue.rescued} of ${summary.rescue.total_scans_window} scans · last ${summary.window_days}d`}
          tone={rescueTone}
        />
        <StatCard
          testId="kpi-ltd-util"
          label="LTD median util"
          value={`${ltdMedian}%`}
          sub={`${summary.cohort_utilization_month_to_date.ltd.n_workspaces} LTD workspaces this month`}
          tone={ltdTone}
        />
        <StatCard
          testId="kpi-usdinr"
          label="USD → INR"
          value={`₹${summary.usd_to_inr}`}
          sub="Live from USD_TO_INR env"
        />
      </div>

      {/* Two-column grid: Cohort util + Weekly scans */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Cohort utilization */}
        <div className="bg-lm-surface border border-lm-border rounded-xl p-4" data-testid="cohort-util-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Allowance utilization</div>
              <div className="font-display font-semibold text-base text-lm-text tracking-tight">By cohort · month-to-date</div>
            </div>
            <Percent className="w-4 h-4 text-lm-text3" />
          </div>
          <table className="w-full text-sm">
            <thead className="text-lm-text3">
              <tr>
                <th className="text-left px-2 py-1.5 text-xs font-medium">Cohort</th>
                <th className="text-right px-2 py-1.5 text-xs font-medium">Median</th>
                <th className="text-right px-2 py-1.5 text-xs font-medium">Mean</th>
                <th className="text-right px-2 py-1.5 text-xs font-medium">Workspaces</th>
              </tr>
            </thead>
            <tbody>
              {["ltd", "sub", "free"].map((k) => {
                const c = summary.cohort_utilization_month_to_date[k] || {};
                return (
                  <tr key={k} className="border-t border-lm-border/60">
                    <td className="px-2 py-2 font-medium uppercase text-xs tracking-widest">{k}</td>
                    <td className="px-2 py-2 text-right font-mono">{c.median_pct ?? 0}%</td>
                    <td className="px-2 py-2 text-right font-mono">{c.mean_pct ?? 0}%</td>
                    <td className="px-2 py-2 text-right font-mono text-lm-text3">{c.n_workspaces ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Weekly scans per active user */}
        <div className="bg-lm-surface border border-lm-border rounded-xl p-4" data-testid="weekly-scans-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Scans per active user</div>
              <div className="font-display font-semibold text-base text-lm-text tracking-tight">Last 4 weeks</div>
            </div>
            <TrendingUp className="w-4 h-4 text-lm-text3" />
          </div>
          <table className="w-full text-sm">
            <thead className="text-lm-text3">
              <tr>
                <th className="text-left px-2 py-1.5 text-xs font-medium">Week of</th>
                <th className="text-right px-2 py-1.5 text-xs font-medium">Scans</th>
                <th className="text-right px-2 py-1.5 text-xs font-medium">Active users</th>
                <th className="text-right px-2 py-1.5 text-xs font-medium">Per user</th>
              </tr>
            </thead>
            <tbody>
              {summary.scans_per_active_user_weekly.map((w) => (
                <tr key={w.week_label} className="border-t border-lm-border/60">
                  <td className="px-2 py-2 font-mono text-xs text-lm-text2">{w.week_label}</td>
                  <td className="px-2 py-2 text-right font-mono">{w.scans}</td>
                  <td className="px-2 py-2 text-right font-mono">{w.active_users}</td>
                  <td className="px-2 py-2 text-right font-mono font-semibold">{w.per_user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Provider cost breakdown */}
      <div className="bg-lm-surface border border-lm-border rounded-xl p-4 mb-6" data-testid="provider-breakdown-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Provider cost breakdown</div>
            <div className="font-display font-semibold text-base text-lm-text tracking-tight">Per scan · per week · window total</div>
          </div>
          <Package className="w-4 h-4 text-lm-text3" />
        </div>
        <table className="w-full text-sm">
          <thead className="text-lm-text3">
            <tr>
              <th className="text-left px-2 py-1.5 text-xs font-medium">Vendor</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">Units/scan</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">$/unit</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">₹/scan</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">₹/week est</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">₹ window</th>
            </tr>
          </thead>
          <tbody>
            {summary.providers.map((p) => (
              <tr key={p.vendor_id} className="border-t border-lm-border/60">
                <td className="px-2 py-2 font-medium">{p.vendor_name}</td>
                <td className="px-2 py-2 text-right font-mono">{p.units_per_scan}</td>
                <td className="px-2 py-2 text-right font-mono text-xs">${p.unit_price_usd}</td>
                <td className="px-2 py-2 text-right font-mono">{fmtINR(p.inr_per_scan, 3)}</td>
                <td className="px-2 py-2 text-right font-mono text-lm-text2">{fmtINR(p.inr_per_week_est, 0)}</td>
                <td className="px-2 py-2 text-right font-mono">{fmtINR(p.inr_total_window, 0)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-lm-border font-semibold">
              <td className="px-2 py-2">Total</td>
              <td colSpan={2}></td>
              <td className="px-2 py-2 text-right font-mono">{fmtINR(summary.cogs.effective_inr_per_scan, 3)}</td>
              <td colSpan={2}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Plans margin */}
      <div className="bg-lm-surface border border-lm-border rounded-xl p-4 mb-6" data-testid="plans-margin-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Margin by plan</div>
            <div className="font-display font-semibold text-base text-lm-text tracking-tight">Net of GST (18%) + gateway (2%)</div>
          </div>
          <Zap className="w-4 h-4 text-lm-text3" />
        </div>
        <table className="w-full text-sm">
          <thead className="text-lm-text3">
            <tr>
              <th className="text-left px-2 py-1.5 text-xs font-medium">Plan</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">Sticker ₹</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">Net ₹</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">Cap</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">Full-use COGS</th>
              <th className="text-right px-2 py-1.5 text-xs font-medium">Margin / Runway</th>
            </tr>
          </thead>
          <tbody>
            {summary.plans_margin.map((p) => (
              <tr key={p.plan_id} className="border-t border-lm-border/60">
                <td className="px-2 py-2">
                  <div className="font-medium">{p.plan_name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">{p.cadence}</div>
                </td>
                <td className="px-2 py-2 text-right font-mono">{fmtINR(p.price_inr, 0)}</td>
                <td className="px-2 py-2 text-right font-mono text-lm-text2">{fmtINR(p.net_inr, 0)}</td>
                <td className="px-2 py-2 text-right font-mono text-lm-text2">{p.scans_cap}</td>
                <td className="px-2 py-2 text-right font-mono">{fmtINR(p.cogs_full_use_inr, 0)}</td>
                <td className="px-2 py-2 text-right font-mono font-semibold">
                  {p.margin_full_use_pct !== undefined
                    ? `${p.margin_full_use_pct}%`
                    : `${p.months_full_use_runway} mo runway`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* API Price Tracker */}
      <div className="bg-lm-surface border border-lm-border rounded-xl p-4 mb-6" data-testid="webhook-health-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Webhook health</div>
            <div className="font-display font-semibold text-base text-lm-text tracking-tight">Duplicate delivery spikes · top 5 per gateway</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {["stripe", "razorpay"].map((gw) => {
            const h = summary.webhook_health?.[gw] || { total: 0, duplicates: 0, top: [], spike_alarm: false };
            return (
              <div key={gw} data-testid={`webhook-health-${gw}`} className="rounded-lg border border-lm-border p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-lm-text">{gw}</div>
                    <div className="text-[10px] font-mono text-lm-text3">
                      {h.total} events · {h.duplicates} with retries
                    </div>
                  </div>
                  {h.spike_alarm && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                      ⚠ Spike
                    </span>
                  )}
                </div>
                {h.top && h.top.length ? (
                  <table className="w-full text-xs">
                    <thead className="text-lm-text3">
                      <tr>
                        <th className="text-left py-1 font-medium">Event ID</th>
                        <th className="text-right py-1 font-medium">Retries</th>
                        <th className="text-right py-1 font-medium">Last</th>
                      </tr>
                    </thead>
                    <tbody>
                      {h.top.map((t) => (
                        <tr key={t.event_id} className="border-t border-lm-border/60">
                          <td className="py-1.5 font-mono truncate max-w-[180px]" title={t.event_id}>{t.event_id}</td>
                          <td className="py-1.5 text-right font-mono font-semibold tabular-nums">×{t.duplicate_count}</td>
                          <td className="py-1.5 text-right font-mono text-lm-text3">
                            {t.last_delivered_at ? new Date(t.last_delivered_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-xs text-lm-text3 py-2 text-center">No duplicate deliveries — healthy.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* API Price Tracker */}
      <div className="bg-lm-surface border border-lm-border rounded-xl p-4" data-testid="price-tracker-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">API Price Tracker</div>
            <div className="font-display font-semibold text-base text-lm-text tracking-tight">Edit any vendor — economics recalculate on refresh</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-lm-tint/60 text-lm-text3 border-b border-lm-border">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Vendor</th>
                <th className="text-left px-3 py-2 font-medium">Price</th>
                <th className="text-left px-3 py-2 font-medium">Unit</th>
                <th className="text-left px-3 py-2 font-medium">Use case</th>
                <th className="text-left px-3 py-2 font-medium">Updated</th>
                <th className="text-right px-3 py-2 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => <VendorRow key={v.vendor_id} v={v} onSaved={load} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
